import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { Sparkles, X, Send, Loader2, Check, Wand2, RotateCcw } from 'lucide-react';
import { API_URL } from '../utils/api';
import { useCopilot } from './CopilotContext';

const GENERIC_SUGGESTIONS = ['Mit tudok ezen az oldalon csinálni?', 'Melyik ügyfélnek van lejárt tartozása?', 'Mutasd a szolgáltatás-katalógust'];

function ActionCard({ action, onApply, applied }) {
  return (
    <div className="mt-2 rounded-xl border p-2.5 text-xs" style={{ borderColor: 'var(--accent-copper-soft, #F1D9C8)', background: 'var(--accent-copper-soft, #FBF1EA)' }} data-testid="copilot-action-card">
      <div className="flex items-start gap-2">
        <Wand2 size={13} className="mt-0.5 flex-shrink-0" style={{ color: 'var(--accent-copper, #B85A26)' }} />
        <p className="flex-1 text-slate-700">{action.label || action.name}</p>
        <button type="button" onClick={onApply} disabled={applied} data-testid="copilot-action-apply"
          className="inline-flex items-center gap-1 px-2.5 h-7 rounded-full text-[11px] font-semibold text-white disabled:opacity-60"
          style={{ background: applied ? '#059669' : 'var(--accent-copper, #B85A26)' }}>
          {applied ? <><Check size={11} /> Kész</> : 'Alkalmaz'}
        </button>
      </div>
    </div>
  );
}

export default function CopilotDrawer() {
  const cp = useCopilot();
  const location = useLocation();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [applied, setApplied] = useState({});
  const listRef = useRef(null);
  const page = cp?.page;
  const spec = page?.ref?.current;
  const pageId = page?.id || `route:${location.pathname}`;
  const sessionId = cp?.getSession(pageId);

  useEffect(() => {
    if (!cp?.open) return;
    setMessages([]); setApplied({});
    if (!sessionId) return;
    axios.get(`${API_URL}/api/copilot/history`, { params: { session_id: sessionId }, withCredentials: true })
      .then((r) => setMessages(r.data.messages || [])).catch(() => {});
  }, [cp?.open, pageId, sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { listRef.current?.scrollTo({ top: 1e9, behavior: 'smooth' }); }, [messages, busy]);

  if (!cp) return null;

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || busy) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: msg }]);
    setBusy(true);
    try {
      const actions = Object.entries(spec?.actions || {}).map(([name, a]) => ({ name, description: a.description, params: a.params }));
      const body = { session_id: sessionId || undefined, message: msg, page: { route: location.pathname, title: spec?.title || document.title, context: spec?.getContext ? spec.getContext() : {}, actions } };
      const r = await axios.post(`${API_URL}/api/copilot/chat`, body, { withCredentials: true });
      cp.setSession(pageId, r.data.session_id);
      setMessages((m) => [...m, { role: 'assistant', content: r.data.reply, actions: r.data.actions || [] }]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Copilot hiba');
      setMessages((m) => [...m, { role: 'assistant', content: 'Sajnálom, most nem sikerült válaszolni. Próbáld újra.', actions: [] }]);
    } finally { setBusy(false); }
  };

  const apply = (mi, ai, action) => {
    const handler = spec?.actions?.[action.name];
    if (!handler) { toast.error(`Ez az akció itt nem érhető el: ${action.name}`); return; }
    try {
      handler.run(action.args || {});
      setApplied((a) => ({ ...a, [`${mi}-${ai}`]: true }));
      toast.success('Alkalmazva');
    } catch (e) { toast.error('Nem sikerült alkalmazni'); }
  };

  const suggestions = spec?.suggestions?.length ? spec.suggestions : GENERIC_SUGGESTIONS;

  return (
    <>
      {!cp.open && (
        <button type="button" onClick={() => cp.setOpen(true)} data-testid="copilot-fab" aria-label="Acountix Copilot"
          className="fixed right-4 bottom-20 md:right-6 md:bottom-6 z-[10001] w-12 h-12 rounded-full shadow-lg flex items-center justify-center text-white transition-transform hover:scale-105 active:scale-95"
          style={{ background: 'linear-gradient(135deg, #B85A26 0%, #94461C 100%)' }}>
          <Sparkles size={20} />
        </button>
      )}
      {cp.open && (
        <aside data-testid="copilot-drawer"
          className="fixed z-[10000] right-0 top-0 h-full w-full sm:w-[400px] bg-white border-l border-slate-200 shadow-2xl flex flex-col animate-fade-in">
          <header className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
            <span className="w-8 h-8 rounded-xl flex items-center justify-center text-white" style={{ background: 'linear-gradient(135deg, #B85A26, #94461C)' }}><Sparkles size={15} /></span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-slate-800 font-heading">Acountix Copilot</p>
              <p className="text-[11px] text-slate-500 truncate" data-testid="copilot-page-title">{spec?.title || 'Általános segítség'}</p>
            </div>
            {sessionId && <button type="button" title="Új beszélgetés" onClick={() => { cp.setSession(pageId, null); setMessages([]); }} className="w-7 h-7 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500" data-testid="copilot-new-session"><RotateCcw size={14} /></button>}
            <button type="button" onClick={() => cp.setOpen(false)} className="w-7 h-7 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500" data-testid="copilot-close"><X size={16} /></button>
          </header>
          <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid="copilot-messages">
            {messages.length === 0 && (
              <div className="text-xs text-slate-500 space-y-2">
                <p>Kérdezz vagy kérj kitöltést — ismerem a nyitott oldalt, az ügyfeleidet, korábbi ajánlataidat és a katalógusodat.</p>
                <div className="flex flex-wrap gap-1.5">
                  {suggestions.map((s, i) => (
                    <button key={i} type="button" onClick={() => send(s)} data-testid={`copilot-suggestion-${i}`}
                      className="text-[11px] px-2.5 py-1 rounded-full border border-slate-200 hover:bg-slate-50 text-slate-700 text-left">{s}</button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, mi) => (
              <div key={mi} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[92%] rounded-2xl px-3 py-2 text-[13px] leading-relaxed whitespace-pre-wrap ${m.role === 'user' ? 'bg-slate-800 text-white' : 'bg-slate-50 text-slate-800 border border-slate-100'}`}
                  data-testid={`copilot-msg-${m.role}`}>
                  {m.content}
                  {m.role === 'assistant' && (m.actions || []).map((a, ai) => (
                    <ActionCard key={ai} action={a} applied={!!applied[`${mi}-${ai}`]} onApply={() => apply(mi, ai, a)} />
                  ))}
                </div>
              </div>
            ))}
            {busy && <div className="flex items-center gap-2 text-xs text-slate-500"><Loader2 size={13} className="animate-spin" /> Gondolkodom…</div>}
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-slate-100 flex items-end gap-2">
            <textarea value={input} onChange={(e) => setInput(e.target.value)} rows={2} data-testid="copilot-input"
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Pl. írd meg a bevezetőt… / tegyél be 3 tételt… / kapcsold ki a tételsorokat"
              className="flex-1 resize-none rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-100" />
            <button type="submit" disabled={busy || !input.trim()} data-testid="copilot-send"
              className="w-10 h-10 rounded-xl flex items-center justify-center text-white disabled:opacity-50" style={{ background: 'var(--accent-copper, #B85A26)' }}>
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            </button>
          </form>
        </aside>
      )}
    </>
  );
}
